import * as React from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactFilters,
  CompactMenu,
  CompactSearch,
  CompactTitle,
  DataTable,
  FilterBox,
  FilterChip,
  HelpPopover,
  PageHeader,
  SelectChip,
  STICKY_TABLE_TOP,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  WorkControls,
  PaneControl,
  SplitList,
  openRow,
  usePanePrefs,
  useOpenRecord
} from '@/design';
import type { IPage } from '../api/oversight-contracts';
import { secAdvApi } from '../api/sec-adv-api';
import type { IAdminSecAdvFirmRow, ISecAdvFirmQuery, SecAdvFacetField } from '../api/sec-adv-contracts';
import {
  LoadError,
  PastTheEnd,
  SEC_ADV_FIRMS_PATH,
  SEC_ADV_STATUS_PATH,
  SEC_ADV_TABS,
  SectionTabs,
  useAdminData
} from '../components/admin-page';
import { firmCardPath } from '../components/firm-link';
import { ListArea, usePaneRead } from '../components/list-area';
import { RowNameClicks } from '../components/row-name-click';
import type { ICardLinkState } from '../components/participant-link';
import { useSecAdvCatalogue } from '../components/sec-adv-values';
import { SignalChips, StateCards, type IStateSignal } from '../components/state-cards';
import { useOwnColumns } from './lists/work-page/columns';
import { WorkPageHeaderActions, useReadAt } from './lists/work-page/header-actions';
import { MAX_SHOWN, WorkPager, dropPaging, readItemPages, useWorkPaging } from './lists/work-page/pager';
import {
  FIRM_CARDS,
  FIRM_SIGNALS,
  cardOf,
  signalWords,
  signalsOf,
  signalsOfCard,
  useFirmCounts
} from './sec-adv/card-signals';
import { ExportButton } from './sec-adv/export-dialog';
import {
  AumChip,
  aumSummary,
  CheckList,
  ParamChip,
  firstDir,
  summaryOf,
  type IColumnFunnel
} from './sec-adv/firm-filters';
import { openColumn, tableColumn, useOfferedFields } from './sec-adv/list-columns';
import {
  DEFAULT_COLUMNS,
  FIRM_TYPE_OPTIONS,
  LIST_FIELDS,
  SURVEY_OPTIONS,
  type IListField
} from './sec-adv/list-fields';
import {
  ITEM_5G,
  lastListPath,
  optionLabel,
  parseAmount,
  registerSummary,
  rememberListSearch,
  yesNoOptions
} from './sec-adv/register';
import { FacetValues } from './sec-adv/value-filter';

/**
 * The SEC ADV register's firms (SR-423) as a work page (`docs/superpowers/specs/2026-10-28-work-pages-design.md`)
 * of the work-item-backed family: its own route — `GET /api/v1/admin/sec-adv/firms` under
 * `registry.browse` — whose every filter, sort, card and signal is a query parameter, the headers'
 * funnels and the filter line's chips written here.
 *
 * **Cards** are the register cut four ways — All firms, SEC-registered, Exempt reporting advisers,
 * Our firms — sent as `card=`; their signals, pressed, as `signal=` repeated, a firm kept where it
 * raises any. The counts are one read (`useFirmCounts`) under the search and the filters, never the
 * card or the signals.
 *
 * **The list is the URL** — the search, the card, the signals, every filter, the sort and the window
 * (`page`, `size`) — so a list can be pasted, Back restores it, and the address it was last left at
 * is kept for the tab (`rememberListSearch`): the firm page's breadcrumb and the menu's entry return
 * to it. Per page, the columns and the card size are the viewer's, on this browser. **The search
 * reads as it is typed** — a quarter-second after the last key, replacing the address — where the
 * work pages submit theirs: the ticket asks for it.
 *
 * **The columns are survey-api's list fields and any catalogue attribute** (`useOwnColumns`), the
 * raw ones asked for through `columns=`. ↗ and the firm's name stay first and frozen on a sideways
 * scroll; a value taken from Form ADV links to that answer in the firm's filing.
 */
export function AdminSecAdvFirmsPage() {
  const location = useLocation();
  // Beside the list as *Opens in* says — its column, or a sheet over this page (SR-530).
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const catalogue = useSecAdvCatalogue().data;
  const state = listStateOf(params);
  // A firm opens beside the register — the register its column, from 1440 px — or in a new tab, by
  // *Opens in* (SR-530); a plain click on its name too, Ctrl/⌘ keeping the link's.
  const panePrefs = usePanePrefs();
  const besideAt = (path: string) =>
    openRecord(path, { search: location.search, list: true } satisfies ICardLinkState);
  const beside = (firm: IAdminSecAdvFirmRow) => besideAt(firmCardPath(firm.firmCrd));
  // The firm's name alone — its card's own address; a value's link to an answer in the filing
  // (`/filings?item=…`) keeps the link's own way.
  const isFirmName = (link: HTMLAnchorElement) => FIRM_CARD.test(link.pathname) && !link.search;

  React.useEffect(() => rememberListSearch(location.search), [location.search]);

  const paging = useWorkPaging({ perPages: PER_PAGES });
  const { page, perPage, pages } = paging;
  const offered = useOfferedFields(catalogue, state.q);
  const chosen = useOwnColumns<IAdminSecAdvFirmRow>('sec-adv', offered);

  const query = queryOf(state, chosen.extras);
  const asked = JSON.stringify([query, page, perPage, pages]);
  const { data, error, reading, reload } = useAdminData<IAnswered>(
    async (signal) => ({
      asked,
      page: await readItemPages(
        (at, pageSize) => secAdvApi.listFirms({ ...query, page: at, pageSize }, signal),
        page,
        perPage,
        pages,
        MAX_SHOWN
      )
    }),
    [asked]
  );
  // The last answer while the next is out; nothing once this query's read has failed. An error
  // stands only while no newer read is out.
  const failed = error !== null && !reading;
  const shown = failed && data?.asked !== asked ? null : data;
  const counts = useFirmCounts(query);
  const status = useAdminData((signal) => secAdvApi.getStatus(signal), []);
  const readAt = useReadAt(data);

  /** A change of search or filter starts at the top — made on the address as it is when it lands,
   *  so the search's delayed write never undoes a filter set meanwhile. */
  function update(mutate: (next: URLSearchParams) => void, replace = false) {
    setParams(
      (current) => {
        const next = dropPaging(new URLSearchParams(current));
        mutate(next);
        return next;
      },
      { replace }
    );
  }
  const search = (text: string) =>
    update((next) => (text.trim() === '' ? next.delete('q') : next.set('q', text)), true);
  const setList = (name: string, values: string[]) =>
    update((next) => {
      next.delete(name);
      for (const value of values) next.append(name, value);
    });
  const setOne = (name: string, value: Nullable<string>) =>
    update((next) => (value === null || value === '' ? next.delete(name) : next.set(name, value)));
  // A press on the column in force turns its order round; on another, sorts by it its first way.
  const sortBy = (field: IListField) =>
    update((next) => {
      const sorted = sortedOf(state, field);
      next.set('sort', field.key);
      next.set('dir', sorted === null ? firstDir(field) : sorted === 'asc' ? 'desc' : 'asc');
    });

  const services = yesNoOptions(catalogue, ITEM_5G);
  const clearAll = () =>
    setParams((current) => {
      const next = new URLSearchParams();
      for (const name of ['card', 'sort', 'dir']) {
        const value = current.get(name);
        if (value !== null) next.set(name, value);
      }
      return next;
    });

  // The column funnels: each a page key, its values read from the facets.
  const [funnelOpen, setFunnelOpen] = React.useState<Nullable<string>>(null);
  // A funnel's vocabulary: Services' from the catalogue, the rest fixed.
  const knownOf = (key: string) =>
    FUNNELS[key].facet === 'services' ? services : (FUNNELS[key].known ?? []);
  const funnelBody = (key: string) => {
    const spec = FUNNELS[key];
    const known = knownOf(key);
    return (
      <FacetValues
        label={spec.label}
        field={spec.facet}
        query={query}
        chosen={state.lists[spec.param]}
        known={known}
        labelOf={(value) => optionLabel(known, value)}
        onChange={(values) => setList(spec.param, values)}
      />
    );
  };
  const funnelSaid = (key: string) =>
    summaryOf(state.lists[FUNNELS[key].param].map((value) => optionLabel(knownOf(key), value))) || null;
  const funnelOf = (key: string): IColumnFunnel | undefined =>
    FUNNELS[key] && {
      code: key,
      said: funnelSaid(key),
      body: funnelBody(key),
      open: funnelOpen === key,
      onOpenChange: (open) => setFunnelOpen(open ? key : null)
    };

  const own = OWN_FIELDS.map((field) => {
    const optional = !DEFAULT_COLUMNS.includes(field.key);
    return {
      ...tableColumn(field, {
        q: state.q,
        catalogue,
        sorted: sortedOf(state, field),
        onSort: () => sortBy(field),
        funnel: funnelOf(field.key)
      }),
      optional,
      hidden: optional
    };
  });
  const columns = chosen.resolve(own);
  const total = counts.counts?.cards.all?.count ?? null;

  const tableState: DataTableState | undefined =
    shown === null
      ? failed
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : shown.page.items.length === 0
        ? {
            kind: 'empty',
            content:
              shown.page.total > 0 ? (
                <PastTheEnd
                  page={shown.page.page}
                  pageSize={shown.page.pageSize}
                  total={shown.page.total}
                  counted={['firm', 'firms']}
                  onPage={paging.setPage}
                />
              ) : state.signals.length > 0 ? (
                'No firm matches the signals pressed.'
              ) : (
                'No firm matches.'
              )
          }
        : undefined;

  // Where the page's own controls end: scrolled under the app bar, the compact bar stands for them.
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  // The controls drawn twice — in the page, and slimmed in the compact bar once they scroll away.
  const refresh = {
    reading: reading || counts.reading,
    readAt,
    onClick: () => {
      void reload();
      void counts.reload();
      void status.reload();
    }
  };
  const help = <AboutRegister />;
  // Where the cards cannot hold "Exempt reporting advisers" on one row, that card says "ERA".
  const [cardsBox, setCardsBox] = React.useState<Nullable<HTMLElement>>(null);
  const narrow = useNarrower(cardsBox, WHOLE_CARDS_WIDTH);
  const cardEntries = FIRM_CARDS.map((spec) => {
    const next = dropPaging(new URLSearchParams(params));
    if (spec.key === 'all') next.delete('card');
    else next.set('card', spec.key);
    const current = state.card === spec.key;
    const read = counts.counts?.cards[spec.key];
    // Unread, the open card's count is the list's own total while no signal narrows it — the
    // total of this query, never the last one's.
    const count =
      read?.count ??
      (current && state.signals.length === 0 && shown?.asked === asked ? shown.page.total : null);
    const signals: IStateSignal[] = read
      ? signalsOfCard(spec.key).flatMap((signal) => {
          const counted = read.signals[signal.key];
          if (typeof counted !== 'number') return [];
          const active = current && state.signals.includes(signal.key);
          return [
            {
              key: signal.key,
              label: signal.label,
              short: signal.short,
              tone: signal.tone,
              count: counted,
              active,
              toggleName: `Show ${counted.toLocaleString('en-US')} ${signal.words} in ${spec.label}`,
              onToggle: () => {
                const pressed = new URLSearchParams(next);
                const kept = state.signals.filter((key) => key !== signal.key);
                pressed.delete('signal');
                for (const key of active ? kept : [...kept, signal.key]) pressed.append('signal', key);
                setParams(pressed);
              }
            }
          ];
        })
      : [];
    return {
      key: spec.key,
      to: { search: next.toString() },
      current,
      label: narrow && spec.short ? spec.short : spec.label,
      count: count?.toLocaleString('en-US') ?? '…',
      hint: spec.hint,
      tooltip: `${spec.label} — ${spec.hint}`,
      signals,
      share: count !== null && total && spec.key !== 'all' ? count / total : undefined,
      data: { 'data-card': spec.key },
      countData: { 'data-count': count?.toString() ?? '' }
    };
  });
  const cards = <StateCards label="Show" compact cards={cardEntries} />;
  const openCard = cardEntries.find((entry) => entry.current) ?? cardEntries[0];
  const signalChips = (
    <SignalChips
      chips={state.signals.map((key) => ({
        key,
        words: signalWords(key),
        clearName: `Show every firm, not only ${signalWords(key)}`,
        onClear: () => setList('signal', state.signals.filter((one) => one !== key))
      }))}
    />
  );
  const columnsButton = chosen.button(own);
  const exported = exportWindow(page, perPage, pages, shown?.page.items.length ?? 0);
  const exportButton = (
    <ExportButton
      query={{ ...query, page: exported.page, pageSize: exported.pageSize }}
      fields={columns.map((column) => column.id)}
      pageLabel={exported.label}
    />
  );
  const lineChips = LINE_FUNNELS.filter((key) => state.lists[FUNNELS[key].param].length > 0);
  const paramChips = lineChips.map((key) => (
    <ParamChip
      key={key}
      words={`${FUNNELS[key].label}: ${funnelSaid(key)}`}
      body={funnelBody(key)}
      onPress={() => {
        if (document.querySelector(`[data-funnel="${CSS.escape(key)}"]`) === null) return false;
        setFunnelOpen(key);
        return true;
      }}
      onRemove={() => setList(FUNNELS[key].param, [])}
    />
  ));
  // Every filter applied, as a chip with its value — the page's row under the toolbar and the
  // compact bar's second row alike: a quick filter's press opens its control (`open`, by its
  // name), a funnel's its column's.
  const toolbar = useWorkToolbar();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const openFilters = () => {
    setFiltersOpen(true);
    return true as const;
  };
  const aum = aumSummary(state.aumMin, state.aumMax);
  const quickChipsWith = (open: (label: string) => true): ICompactChip[] => [
    ...(state.reported !== null
      ? [
          {
            key: 'reported',
            chip: (
              <ParamChip
                words={`Last reported: in the past ${state.reported} months`}
                body={null}
                onPress={() => open('Last reported')}
                onRemove={() => setOne('reported', null)}
              />
            )
          }
        ]
      : []),
    ...(aum !== null
      ? [
          {
            key: 'aum',
            chip: (
              <ParamChip
                words={`Total AUM: ${aum}`}
                body={null}
                onPress={() => open('Total AUM')}
                onRemove={() =>
                  update((next) => {
                    next.delete('aumMin');
                    next.delete('aumMax');
                  })
                }
              />
            )
          }
        ]
      : []),
    ...(state.lists.service.length > 0
      ? [
          {
            key: 'service',
            chip: (
              <ParamChip
                words={`Services: ${summaryOf(state.lists.service.map((code) => optionLabel(services, code)))}`}
                body={null}
                onPress={() => open('Services')}
                onRemove={() => setList('service', [])}
              />
            )
          }
        ]
      : [])
  ];
  const appliedChipsWith = (open: (label: string) => true): ICompactChip[] => [
    ...(state.signals.length > 0 ? [{ key: 'signals', chip: signalChips }] : []),
    ...quickChipsWith(open),
    ...lineChips.map((key, index) => ({ key: `funnel-${key}`, chip: paramChips[index] }))
  ];
  const quickCount = quickChipsWith(openFilters).length;
  const appliedChips = appliedChipsWith(openFilters);
  const pageChips = appliedChipsWith(toolbar.openQuick);
  // The toolbar's quick filters, drawn in the page and again in the compact bar.
  const quickFilters = (
    <>
      <SelectChip
        label="Last reported"
        any="Any time"
        value={state.reported}
        options={REPORTED.map((months) => ({
          value: String(months),
          label: `In the past ${months} months`
        }))}
        onChoose={(value) => setOne('reported', value)}
      />
      <AumChip
        min={state.aumMin}
        max={state.aumMax}
        onApply={(min, max) =>
          update((next) => {
            if (min === null) next.delete('aumMin');
            else next.set('aumMin', String(min));
            if (max === null) next.delete('aumMax');
            else next.set('aumMax', String(max));
          })
        }
      />
      <FilterChip
        label="Services"
        summary={summaryOf(state.lists.service.map((code) => optionLabel(services, code)))}
      >
        {services.length === 0 ? (
          <p className="text-muted-foreground">No services to choose from.</p>
        ) : (
          <CheckList
            label="Services"
            options={services}
            chosen={state.lists.service}
            onChange={(values) => setList('service', values)}
          />
        )}
      </FilterChip>
    </>
  );

  return (
    <>
      <PageHeader
        title="SEC ADV register"
        row
        tabs={<SectionTabs tabs={SEC_ADV_TABS} />}
        actions={
          <WorkPageHeaderActions
            summary={registerSummary(status.data?.firmFeed)}
            summaryData={{ 'data-register-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="SEC ADV register, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={
          appliedChips.length > 0 && <CompactChipLine chips={appliedChips} onClearAll={clearAll} />
        }
      >
        <CompactTitle>SEC ADV register</CompactTitle>
        <CompactMenu
          label="Section"
          trigger="Firms"
          items={[
            { key: 'firms', label: 'Firms', to: SEC_ADV_FIRMS_PATH, current: true },
            { key: 'status', label: 'Register status', to: SEC_ADV_STATUS_PATH, current: false }
          ]}
        />
        <CompactMenu
          label="Show"
          chip
          trigger={
            <>
              {FIRM_CARDS.find((spec) => spec.key === openCard.key)?.label}
              <span className="font-semibold tabular-nums">{openCard.count}</span>
            </>
          }
          items={cardEntries.map((entry, index) => ({
            key: entry.key,
            label: FIRM_CARDS[index].label,
            to: entry.to,
            current: entry.current,
            count: entry.count
          }))}
        />
        <CompactDivider />
        <FirmSearch q={state.q} onSearch={search} compact />
        <CompactFilters count={quickCount} open={filtersOpen} onOpenChange={setFiltersOpen}>
          {quickFilters}
        </CompactFilters>
        <span className="flex-1" />
        <CompactDivider />
        {columnsButton}
        {exportButton}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        <div ref={setCardsBox} className="min-w-0">
          {cards}
        </div>

        <WorkControls end={setControlsEnd}>
          <WorkToolbar
            toolbar={toolbar}
            search={<FirmSearch q={state.q} onSearch={search} />}
            quick={quickFilters}
            quickCount={quickCount}
            end={
              <>
                {columnsButton}
                {exportButton}
                <PaneControl prefs={panePrefs} kind="record" />
              </>
            }
          />
          {pageChips.length > 0 && <CompactChipLine label="Filters" chips={pageChips} onClearAll={clearAll} />}
        </WorkControls>

        {failed && shown !== null && (
          <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
        )}

        <RowNameClicks accept={isFirmName} open={besideAt}>
        <DataTable
          label="Firms"
          columns={[openColumn(beside), ...columns]}
          rows={tableState ? null : (shown?.page.items ?? null)}
          rowKey={(firm) => firm.firmCrd}
          onOpen={(firm) => openRow(panePrefs, firmCardPath(firm.firmCrd), () => beside(firm))}
          busy={reading && shown !== null}
          state={tableState}
          stickyTop={STICKY_TABLE_TOP}
        />
        </RowNameClicks>

        {shown && <WorkPager {...paging.pager} total={shown.page.total} busy={reading} sticky />}
      </div>
    </>
  );
}

/**
 * The register and a firm's card as one area (SR-530): a card opened from the register is drawn
 * beside its column — the register under the search it was opened from — from 1440 px, where the
 * card's two-column body still has its room; narrower, the card is alone with *Show list* gone, as
 * SR-423 drew it. *Opens in* "Sheet" draws the card over the register instead.
 */
export function AdminSecAdvFirmsArea() {
  return (
    <ListArea
      param="firmCrd"
      list={SEC_ADV_FIRMS_PATH}
      paneTitle="SEC ADV register"
      pane={(search, selected) => <FirmsPane search={search} selected={selected} />}
      columnFrom={1440}
    />
  );
}

/** The register as the column beside a card: the firm's name, its numbers, under the register's own
 *  query (its search, filters and sort). */
function FirmsPane({ search, selected }: { search: string; selected: string }) {
  const { shown, pager, status } = usePaneRead(search, 'No firm on this list.', (params, page, signal) =>
    secAdvApi.listFirms({ ...queryOf(listStateOf(params), []), page, pageSize: PANE_PAGE }, signal)
  );
  return (
    <SplitList
      label="SEC ADV register"
      selected={selected}
      items={
        shown?.items.map((firm) => ({
          key: firm.firmCrd,
          to: firmCardPath(firm.firmCrd),
          state: { search, list: true } satisfies ICardLinkState,
          primary: firm.businessName ?? firm.legalName,
          secondary: `CRD ${firm.firmCrd}${firm.secNumber ? ` · ${firm.secNumber}` : ''}`
        })) ?? null
      }
      status={status}
      footer={pager}
    />
  );
}

/** A firm card's own address, no tab: what a firm's name links to. */
const FIRM_CARD = new RegExp(`^${SEC_ADV_FIRMS_PATH}/[^/]+$`);

/** The column's page: the register's smallest. */
const PANE_PAGE = 25;

/** The register's root — the menu's entry — opens the list as it was last left in this tab. */
export function AdminSecAdvHome() {
  return <Navigate to={lastListPath()} replace />;
}

/** The search box. It reads as typed: the address follows the draft a quarter-second after the
 *  last key. The draft is held here, so a key re-renders the box and not the page's rows. */
function FirmSearch({
  q,
  onSearch,
  compact = false
}: {
  q: string;
  onSearch: (text: string) => void;
  /** The compact bar's: narrower. */
  compact?: boolean;
}) {
  const [draft, setDraft] = React.useState(q);
  React.useEffect(() => setDraft(q), [q]);
  React.useEffect(() => {
    if (draft === q) return;
    const timer = setTimeout(() => onSearch(draft), 250);
    return () => clearTimeout(timer);
    // `onSearch` writes on the address as it is when it lands.
  }, [draft]);

  if (compact) {
    return (
      <CompactSearch
        label={SEARCH_LABEL}
        placeholder={SEARCH_PLACEHOLDER}
        draft={draft}
        onDraft={setDraft}
        onSubmit={() => onSearch(draft)}
      />
    );
  }
  return (
    <form
      role="search"
      className="w-full min-w-48 sm:w-auto sm:max-w-96 sm:flex-1"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch(draft);
      }}
    >
      <FilterBox
        label={SEARCH_LABEL}
        placeholder={SEARCH_PLACEHOLDER}
        value={draft}
        onChange={setDraft}
        className="block"
        inputClassName="h-9"
      />
    </form>
  );
}

const SEARCH_LABEL = 'Filter firms';
const SEARCH_PLACEHOLDER = 'Filter firms…';

/** The "?": what the page holds and how its controls read. */
function AboutRegister() {
  return (
    <HelpPopover label="About the SEC ADV register" align="end">
      <p>
        Every investment adviser in the SEC&apos;s monthly Form ADV files, refreshed daily. Open a firm
        for its overview, analytics and filings.
      </p>
      <ul className="grid list-disc gap-1 pl-5">
        {FIRM_CARDS.map((card) => (
          <li key={card.key}>
            <span className="font-medium">{card.label}</span> — {card.about}
          </li>
        ))}
      </ul>
      <p>A card&apos;s badges count the firms in it by signal:</p>
      <ul className="grid list-disc gap-1 pl-5">
        {FIRM_SIGNALS.map((signal) => (
          <li key={signal.key}>
            <span className="font-medium">{signal.words}</span> — {signal.about}
          </li>
        ))}
      </ul>
      <p>
        The survey&apos;s are on Our firms alone. Press badges to show the firms raising any of them;
        the counts stay as they are.
      </p>
      <p>
        The search finds every word in a firm&apos;s legal or business name, or its main office&apos;s
        city or state, punctuation ignored; a SEC number in any spelling (<code>801-12345</code>,{' '}
        <code>80112345</code>) and a CRD exactly. Names rank first.
      </p>
      <p>
        A column&apos;s name sorts by it, its funnel filters. A Form ADV value links to that answer in the
        firm&apos;s filing; an exempt reporting adviser reports no AUM.
      </p>
      <p>
        A firm opens beside the register by its first icon — from 1440 px the register stays beside
        it, ↑ and ↓ walk it, Esc comes back — and in a new tab by ↗; a click on the row or the name
        goes where «Opens in» says.
      </p>
    </HelpPopover>
  );
}

// ---------------------------------------------------------------------------
// The address
// ---------------------------------------------------------------------------

const REPORTED = [3, 6, 13, 24];

/** The page's width below which the four compact cards, ERA's named whole, take two rows. */
const WHOLE_CARDS_WIDTH = 1120;

/** Whether `box` is narrower than `width` — followed as it resizes. */
function useNarrower(box: Nullable<HTMLElement>, width: number): boolean {
  const [narrow, setNarrow] = React.useState(false);
  React.useEffect(() => {
    if (!box) return;
    const observer = new ResizeObserver(([entry]) => setNarrow(entry.contentRect.width < width));
    observer.observe(box);
    return () => observer.disconnect();
  }, [box, width]);
  return narrow;
}

/** Per page here: the work pages' three, and this register's longer reads. */
const PER_PAGES: readonly number[] = [25, 50, 100, 500, 1000];


/** The repeated filters, by their URL key. */
const LISTS = ['firmType', 'city', 'state', 'service', 'survey', 'signal'] as const;

interface IListState {
  q: string;
  card: ReturnType<typeof cardOf>;
  signals: string[];
  reported: Nullable<string>;
  aumMin: Nullable<number>;
  aumMax: Nullable<number>;
  lists: Record<(typeof LISTS)[number], string[]>;
  sort: Nullable<string>;
  dir: 'asc' | 'desc';
}

function listStateOf(params: URLSearchParams): IListState {
  const amount = (name: string) => {
    const raw = params.get(name);
    return raw === null ? null : parseAmount(raw);
  };
  return {
    q: params.get('q') ?? '',
    card: cardOf(params.get('card')),
    signals: signalsOf(params),
    reported: params.get('reported'),
    aumMin: amount('aumMin'),
    aumMax: amount('aumMax'),
    lists: Object.fromEntries(LISTS.map((name) => [name, params.getAll(name)])) as IListState['lists'],
    sort: params.get('sort'),
    dir: params.get('dir') === 'asc' ? 'asc' : 'desc'
  };
}

/** The list's state as survey-api's query, the raw attributes shown asked for as `columns`. */
function queryOf(state: IListState, extras: string[]): ISecAdvFirmQuery {
  const months = Number(state.reported);
  return {
    q: state.q,
    card: state.card === 'all' ? undefined : state.card,
    signal: state.signals,
    firmType: state.lists.firmType,
    filedSince: REPORTED.includes(months) ? monthsAgo(months) : undefined,
    aumMin: state.aumMin ?? undefined,
    aumMax: state.aumMax ?? undefined,
    city: state.lists.city,
    state: state.lists.state,
    service: state.lists.service,
    surveyStatus: state.lists.survey,
    sort: state.sort ?? undefined,
    dir: state.sort ? state.dir : undefined,
    columns: extras
  };
}

/**
 * The rows shown as the one page the export asks for, and what its choice says. The window after
 * *Load more* is one page of its whole size where it starts on a multiple of that size — from the
 * top, always; the export takes no offset, so elsewhere it is the window's first page alone, and
 * the choice names that page.
 */
function exportWindow(
  page: number,
  perPage: number,
  pages: number,
  shownRows: number
): { page: number; pageSize: number; label: string } {
  const rows = (count: number) => `${count.toLocaleString('en-US')} ${count === 1 ? 'row' : 'rows'}`;
  if ((page - 1) % pages === 0) {
    const at = (page - 1) / pages + 1;
    return { page: at, pageSize: perPage * pages, label: `This page (${rows(shownRows)})` };
  }
  return { page, pageSize: perPage, label: `Page ${page} (${rows(Math.min(perPage, shownRows))})` };
}

/** Today less `months`, as a day — `yyyy-MM-dd`, local — on the month's last day where it is
 *  shorter: Aug 31 less 6 months is Feb 28. */
function monthsAgo(months: number): string {
  const today = new Date();
  const lastDay = new Date(today.getFullYear(), today.getMonth() - months + 1, 0).getDate();
  const date = Math.min(today.getDate(), lastDay);
  const day = new Date(today.getFullYear(), today.getMonth() - months, date);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

/** A read of the list, with the query it answers. */
interface IAnswered {
  asked: string;
  page: IPage<IAdminSecAdvFirmRow>;
}

// ---------------------------------------------------------------------------
// The columns
// ---------------------------------------------------------------------------

/** A column's funnel: the page key it writes, the facet that counts its values, and — where it has
 *  one — the vocabulary offered while the facets cannot be read. */
const FUNNELS: Record<
  string,
  {
    label: string;
    param: (typeof LISTS)[number];
    facet: SecAdvFacetField;
    known?: { value: string; label: string }[];
  }
> = {
  firmType: { label: 'Registration type', param: 'firmType', facet: 'firmType', known: FIRM_TYPE_OPTIONS },
  officeCity: { label: 'Office city', param: 'city', facet: 'city' },
  officeState: { label: 'Office region', param: 'state', facet: 'state' },
  services: { label: 'Services', param: 'service', facet: 'services' },
  surveyStatus: { label: 'Survey status', param: 'survey', facet: 'surveyStatus', known: SURVEY_OPTIONS }
};

/** The list fields as the page's own columns: the default ones in their order, then the rest. */
const OWN_FIELDS = [
  ...DEFAULT_COLUMNS.flatMap((key) => LIST_FIELDS.filter((field) => field.key === key)),
  ...LIST_FIELDS.filter((field) => !DEFAULT_COLUMNS.includes(field.key))
];

/** The funnels the filter line says as chips — Services is the toolbar's, which says itself. */
const LINE_FUNNELS = ['firmType', 'officeCity', 'officeState', 'surveyStatus'];

/** The order a column is drawn sorted in — with none named, survey-api orders by total AUM, largest
 *  first (a search ranks first). */
function sortedOf(state: IListState, field: IListField): Nullable<'asc' | 'desc'> {
  if (state.sort === field.key) return state.dir;
  return state.sort === null && field.key === 'totalAum' ? 'desc' : null;
}
